import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { scaleLinear } from "d3-scale";
import { interpolateRdYlGn } from "d3-scale-chromatic";
import { formatCurrency } from "@/lib/businesscaseCalculations";

interface MonthlyHeatmapProps {
  yearlyData: Array<{
    year: number;
    cashflowBase: number;
  }>;
}

const MONTHS = ["Jan", "Feb", "Mrt", "Apr", "Mei", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dec"];

// Simulate monthly distribution with seasonal variation
function distributeToMonths(yearlyValue: number): number[] {
  // Energy consumption patterns: higher in winter, lower in summer
  const seasonalFactors = [1.2, 1.15, 1.1, 1.0, 0.9, 0.85, 0.8, 0.85, 0.9, 1.0, 1.1, 1.15];
  const sum = seasonalFactors.reduce((a, b) => a + b, 0);
  
  return seasonalFactors.map(factor => (yearlyValue * factor) / sum);
}

export function MonthlyHeatmap({ yearlyData }: MonthlyHeatmapProps) {
  const [hoveredCell, setHoveredCell] = useState<{ year: number; month: number; value: number } | null>(null);

  if (!yearlyData || yearlyData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span>🔥</span>
            Monthly Cashflow Heatmap
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-[300px] items-center justify-center text-muted-foreground">
            Geen data beschikbaar
          </div>
        </CardContent>
      </Card>
    );
  }

  // Generate monthly data for all years
  const monthlyData = yearlyData.map(yearData => ({
    year: yearData.year,
    months: distributeToMonths(yearData.cashflowBase),
  }));

  // Calculate color scale based on all values
  const allValues = monthlyData.flatMap(y => y.months);
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  
  const colorScale = scaleLinear()
    .domain([minValue, 0, maxValue])
    .range([0, 0.5, 1])
    .clamp(true);

  const getColor = (value: number) => {
    const normalized = colorScale(value) as number;
    return interpolateRdYlGn(normalized);
  };

  const cellWidth = 70;
  const cellHeight = 45;
  const labelWidth = 60;
  const headerHeight = 30;
  const padding = 10;

  const totalWidth = labelWidth + MONTHS.length * cellWidth + padding * 2;
  const totalHeight = headerHeight + monthlyData.length * cellHeight + padding * 2;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span>🔥</span>
          Maandelijkse Cashflow Heatmap
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <svg width={totalWidth} height={totalHeight} className="mx-auto">
            {/* Month headers */}
            <g transform={`translate(${labelWidth + padding}, ${padding})`}>
              {MONTHS.map((month, i) => (
                <text
                  key={month}
                  x={i * cellWidth + cellWidth / 2}
                  y={headerHeight / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="text-xs font-semibold fill-foreground"
                >
                  {month}
                </text>
              ))}
            </g>

            {/* Year labels and cells */}
            {monthlyData.map((yearData, yearIndex) => (
              <g key={yearData.year} transform={`translate(${padding}, ${headerHeight + padding + yearIndex * cellHeight})`}>
                {/* Year label */}
                <text
                  x={labelWidth - 10}
                  y={cellHeight / 2}
                  textAnchor="end"
                  dominantBaseline="middle"
                  className="text-xs font-semibold fill-foreground"
                >
                  {yearData.year}
                </text>

                {/* Month cells */}
                {yearData.months.map((value, monthIndex) => {
                  const isHovered = hoveredCell?.year === yearData.year && hoveredCell?.month === monthIndex;
                  
                  return (
                    <g
                      key={monthIndex}
                      transform={`translate(${labelWidth + monthIndex * cellWidth}, 0)`}
                      onMouseEnter={() => setHoveredCell({ year: yearData.year, month: monthIndex, value })}
                      onMouseLeave={() => setHoveredCell(null)}
                      className="cursor-pointer"
                    >
                      <rect
                        width={cellWidth - 2}
                        height={cellHeight - 2}
                        x={1}
                        y={1}
                        fill={getColor(value)}
                        stroke={isHovered ? "hsl(var(--foreground))" : "hsl(var(--border))"}
                        strokeWidth={isHovered ? 2 : 1}
                        rx={4}
                      />
                      {isHovered && (
                        <text
                          x={cellWidth / 2}
                          y={cellHeight / 2}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="text-[10px] font-bold fill-foreground pointer-events-none"
                        >
                          {formatCurrency(value)}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            ))}
          </svg>
        </div>

        {/* Legend */}
        <div className="mt-6 flex items-center justify-center gap-4">
          <span className="text-xs text-muted-foreground">Negatief</span>
          <div className="flex h-4 w-48 rounded">
            {Array.from({ length: 20 }).map((_, i) => (
              <div
                key={i}
                style={{
                  backgroundColor: interpolateRdYlGn(i / 19),
                  width: `${100 / 20}%`,
                }}
              />
            ))}
          </div>
          <span className="text-xs text-muted-foreground">Positief</span>
        </div>

        {/* Hover tooltip */}
        {hoveredCell && (
          <div className="mt-4 text-center">
            <div className="inline-block rounded-lg border bg-background p-3 shadow-lg">
              <p className="font-semibold">
                {MONTHS[hoveredCell.month]} {hoveredCell.year}
              </p>
              <p className="text-lg">{formatCurrency(hoveredCell.value)}</p>
            </div>
          </div>
        )}

        <div className="mt-4 text-center text-sm text-muted-foreground">
          Maandelijkse cashflow distributie met seizoenspatronen
        </div>
      </CardContent>
    </Card>
  );
}
